
/** How far a creature walks per full leg cycle. A stride, in millimetres. */
const STRIDE_MM = 900;

/** How far a leg swings, as a fraction of the body's length. */
const SWING = 0.3;

/** Body bob across a stride, as a fraction of the body's width. Small: seen from
 *  above there is no vertical, so this reads as weight shifting rather than as
 *  bouncing. */
const BOB = 0.06;

export interface CreaturePalette {
  /** Fur, sampled from the original's skin. */
  readonly body: string;
  /** Legs, tail and the ground shadow. */
  readonly dark: string;
}

export const DEFAULT_PALETTE: CreaturePalette = { body: "#8a7a63", dark: "#554b3d" };

export type CreatureShape = "quadruped" | "humanoid";

export interface CreatureFrame {
  /** Screen position of the creature's centre, in device-independent pixels. */
  readonly x: number;
  readonly y: number;
  /** Half the body's length, in pixels. Everything else is a fraction of it. */
  readonly radius: number;
  /** Facing, in radians, screen space, clockwise from east. */
  readonly angle: number;
  /** Millimetres travelled since this creature appeared. Drives the gait. */
  readonly distanceMm: number;
  /** True when standing still: the legs settle rather than freezing mid-stride. */
  readonly resting: boolean;
  readonly palette: CreaturePalette;
}

export function gaitPhase(distanceMm: number, resting: boolean): number {
  if (resting) return 0;
  const cycles = distanceMm / STRIDE_MM;
  return cycles - Math.floor(cycles);
}

/** A leg's offset along the body, for a phase and a side. */
export function legSwing(phase: number, opposed: boolean): number {
  const shifted = opposed ? phase + 0.5 : phase;
  return Math.sin(shifted * Math.PI * 2) * SWING;
}

export function drawQuadruped(ctx: CanvasRenderingContext2D, frame: CreatureFrame): void {
  const { radius, palette } = frame;
  const phase = gaitPhase(frame.distanceMm, frame.resting);

  ctx.save();
  ctx.translate(frame.x, frame.y);
  ctx.rotate(frame.angle);

  // A soft ellipse under everything. Seen from directly above there is no cast
  // shadow to speak of, but a creature drawn with none sits *on* the ground
  // rather than *in* the world.
  ctx.globalAlpha = 0.28;
  ctx.fillStyle = "#000000";
  ellipse(ctx, 0, radius * 0.1, radius * 1.05, radius * 0.62);
  ctx.globalAlpha = 1;

  ctx.fillStyle = palette.dark;
  const legWidth = radius * 0.15;
  const legLength = radius * 0.3;
  for (const [along, side, opposed] of [
    [0.42, -0.58, false],
    [0.42, 0.58, true],
    [-0.4, -0.6, true],
    [-0.4, 0.6, false],
  ] as const) {
    const swing = legSwing(phase, opposed) * radius;
    ellipse(ctx, along * radius + swing, side * radius, legLength, legWidth);
  }

  // Tail, swinging opposite the body's sway so the animal reads as counter-
  // balancing rather than as one rigid piece.
  const sway = Math.sin(phase * Math.PI * 2) * radius * 0.18;
  ctx.strokeStyle = palette.dark;
  ctx.lineWidth = Math.max(1, radius * 0.12);
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(-radius * 0.85, 0);
  ctx.quadraticCurveTo(-radius * 1.15, sway, -radius * 1.35, sway * 1.6);
  ctx.stroke();

  // Body, bobbing side to side with the stride.
  const bob = Math.sin(phase * Math.PI * 4) * radius * BOB;
  // Longer and narrower than the first pass. A quadruped seen from above is
  // mostly length; at 0.92 by 0.5 it read as a loaf.
  ctx.fillStyle = palette.body;
  ellipse(ctx, -radius * 0.05, bob, radius * 0.88, radius * 0.42);

  // Head and muzzle. The muzzle is what makes the front end read as a front end
  // at ten pixels across, which is the size this is usually drawn at.
  ctx.beginPath();
  ctx.arc(radius * 0.72, bob * 0.5, radius * 0.32, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = palette.dark;
  ellipse(ctx, radius * 1.02, bob * 0.5, radius * 0.26, radius * 0.14);

  ctx.restore();
}

export function drawHumanoid(ctx: CanvasRenderingContext2D, frame: CreatureFrame): void {
  const { radius, palette } = frame;
  const phase = gaitPhase(frame.distanceMm, frame.resting);

  ctx.save();
  ctx.translate(frame.x, frame.y);
  ctx.rotate(frame.angle);

  ctx.globalAlpha = 0.28;
  ctx.fillStyle = "#000000";
  ellipse(ctx, 0, radius * 0.1, radius * 0.8, radius * 0.7);
  ctx.globalAlpha = 1;

  // Arms first, so the shoulders sit over them.
  ctx.fillStyle = palette.dark;
  const armLength = radius * 0.42;
  const armWidth = radius * 0.2;
  for (const [side, opposed] of [
    [-0.62, false],
    [0.62, true],
  ] as const) {
    const swing = legSwing(phase, opposed) * radius * 1.2;
    ellipse(ctx, swing, side * radius, armLength, armWidth);
  }

  const bob = Math.sin(phase * Math.PI * 4) * radius * BOB;
  ctx.fillStyle = palette.body;
  // Shoulders: wider across than fore-and-aft, which is what a person is.
  ellipse(ctx, -radius * 0.05, bob, radius * 0.46, radius * 0.62);

  // Head, forward of centre so the silhouette has a front.
  ctx.beginPath();
  ctx.arc(radius * 0.16, bob, radius * 0.34, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

export function drawCreature(
  ctx: CanvasRenderingContext2D,
  shape: CreatureShape,
  frame: CreatureFrame,
): void {
  if (shape === "quadruped") drawQuadruped(ctx, frame);
  else drawHumanoid(ctx, frame);
}

export class GaitTracker {
  private readonly walked = new Map<number, number>();
  private readonly last = new Map<number, { x: number; y: number }>();

  /** Fold in a position and return the total distance travelled, in mm. */
  advance(id: number, xMm: number, yMm: number): number {
    const previous = this.last.get(id);
    let total = this.walked.get(id) ?? 0;

    if (previous !== undefined) {
      const step = Math.hypot(xMm - previous.x, yMm - previous.y);
      // A respawn moves a character hundreds of metres in one frame, and folding
      // that into the stride spins the legs for a moment. Anything past a
      // sprint-frame is a relocation, not a step.
      if (step < MAX_STEP_MM) total += step;
    }

    this.walked.set(id, total);
    this.last.set(id, { x: xMm, y: yMm });
    return total;
  }

  peek(id: number): number {
    return this.walked.get(id) ?? 0;
  }

  /** Forget everyone not in `live`. Called once a frame with what was drawn. */
  prune(live: ReadonlySet<number>): void {
    for (const id of this.walked.keys()) {
      if (!live.has(id)) {
        this.walked.delete(id);
        this.last.delete(id);
      }
    }
  }

  get tracked(): number {
    return this.walked.size;
  }
}

/** A frame of running is 275 mm; anything past a metre is a teleport. */
const MAX_STEP_MM = 1_000;

function ellipse(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radiusX: number,
  radiusY: number,
): void {
  ctx.beginPath();
  ctx.ellipse(x, y, Math.max(0.5, radiusX), Math.max(0.5, radiusY), 0, 0, Math.PI * 2);
  ctx.fill();
}
